import type { RecentSearch, RecentSearchResultRef, SearchResult } from '../types';
import { getPlaceDetails, getRecentSearches } from '../services/api';

const MAP_STORE_KEY = 'map-store';

/** Place ref kept in session memory only — no coordinates or address. */
export function slimRecentSearchResult(
  source: {
    id: string;
    name: string;
    category: RecentSearchResultRef['category'];
    type?: SearchResult['type'] | string;
  },
): RecentSearchResultRef {
  return {
    id: source.id,
    name: source.name,
    category: source.category,
    type: source.type as SearchResult['type'] | undefined,
  };
}

/** @deprecated Legacy map-store entries may contain coordinates; strip before use. */
export function sanitizePersistedRecentSearch(entry: RecentSearch): RecentSearch {
  if (!entry.result) return entry;
  return {
    id: entry.id,
    query: entry.query,
    timestamp: entry.timestamp,
    result: slimRecentSearchResult(entry.result),
  };
}

export function serverRecentToLocal(entry: {
  id: string;
  query: string;
  timestamp: string | number;
  result?: {
    id: string;
    name: string;
    category?: string;
  };
}): RecentSearch {
  return {
    id: entry.id,
    query: entry.query,
    timestamp:
      typeof entry.timestamp === 'string'
        ? new Date(entry.timestamp).getTime()
        : Number(entry.timestamp),
    result: entry.result
      ? slimRecentSearchResult({
          id: entry.result.id,
          name: entry.result.name,
          category: (entry.result.category ?? 'unknown') as RecentSearchResultRef['category'],
        })
      : undefined,
  };
}

/** Remove legacy localStorage recent searches (pre-session-only storage). */
export function purgeLegacyMapStoreRecents(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(MAP_STORE_KEY);
  } catch {
    // ignore quota / private mode
  }
}

export function clearSessionRecentSearches(): void {
  void import('../stores/mapStore').then(({ useMapStore }) => {
    useMapStore.setState({ recentSearches: [] });
  });
}

/** Load account search history into session memory (signed-in + searchHistory only). */
export async function hydrateRecentSearchesFromServer(): Promise<void> {
  const [{ useAuthStore }, { useSettingsStore }, { useMapStore }] = await Promise.all([
    import('../stores/authStore'),
    import('../stores/settingsStore'),
    import('../stores/mapStore'),
  ]);

  if (!useAuthStore.getState().isAuthenticated) return;
  if (!useSettingsStore.getState().settings.searchHistory) return;

  try {
    const remote = await getRecentSearches(5);
    useMapStore.setState({
      recentSearches: remote.map(serverRecentToLocal),
    });
  } catch {
    // ignore — session recents still work for current tab
  }
}

/** Resolve a recent place ref via API when directions need coordinates. */
export async function resolveRecentSearchLocation(
  placeId: string,
): Promise<{ name: string; coordinates: { lat: number; lng: number } } | null> {
  try {
    const details = await getPlaceDetails(placeId);
    return {
      name: details.name,
      coordinates: {
        lat: details.coordinates.latitude,
        lng: details.coordinates.longitude,
      },
    };
  } catch {
    return null;
  }
}

purgeLegacyMapStoreRecents();
